// components/FeaturedProvidersSection.jsx
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import "../featuredAcademiesSection/FeaturedAcademiesSection.css";
import TutorCard from "../TutorCard/TutorCard";
import TutorCardSkeleton from "../TutorCard/TutorCardSkeleton";
import api from "../../utils/api";

// ── fetcher — same endpoint & response shape as before, extra query params
// select the "verified" or "recent" slice the backend now supports ──────────
export const fetchProviders = (role, extraQuery = "") =>
  api
    .get(`/users/tutors-academies?role=${role}&limit=8&page=1${extraQuery}`)
    .then((r) => r.data.data || []);

const scoreProvider = (p) => {
  let score = 0;

  if (p.isVerified) score += 100;

  score += (p.averageRating || 0) * 10;

  score += p.reviewCount || 0;

  return score;
};

// Renders one titled grid of provider cards — used for both the "Verified"
// and "Recently Joined" homepage sections.
export const ProviderGrid = ({
  title,
  subtitle,
  providers,
  loading,
  showJoinedDate = false,
  emptyMessage,
}) => (
  <div className="featured-subsection">
    <h2 className="featured-section-title">{title}</h2>
    {subtitle && <p className="featured-section-subtitle">{subtitle}</p>}

    <div className="cards-grid academies-grid">
      {loading ? (
        [...Array(8)].map((_, i) => <TutorCardSkeleton key={`skeleton-${i}`} />)
      ) : providers.length > 0 ? (
        providers.map((provider, index) => (
          // Pass index so TutorCard knows whether to lazy-load its image.
          // The first 4 cards are above the fold → eager; rest → lazy.
          <TutorCard
            key={provider._id}
            tutor={provider}
            imagePriority={index < 4 ? "eager" : "lazy"}
            showJoinedDate={showJoinedDate}
          />
        ))
      ) : (
        <p className="featured-grid-empty">{emptyMessage}</p>
      )}
    </div>
  </div>
);

// Verified providers only — confirmed credentials, ranked by rating. The
// "Recently Joined" grid lives in its own RecentlyJoinedSection component so
// it can be placed further down the homepage; both still dedupe against
// each other via the shared query keys/cache.
const FeaturedProvidersSection = () => {
  const { data: verifiedTutors = [], isLoading: loadingVerifiedTutors } =
    useQuery({
      queryKey: ["featured-providers", "verified", "tutor"],
      queryFn: () => fetchProviders("tutor", "&verified=true"),
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    });

  const { data: verifiedAcademies = [], isLoading: loadingVerifiedAcademies } =
    useQuery({
      queryKey: ["featured-providers", "verified", "institution"],
      queryFn: () => fetchProviders("institution", "&verified=true"),
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    });

  const loadingVerified = loadingVerifiedTutors || loadingVerifiedAcademies;

  const verifiedProviders = useMemo(() => {
    const combined = [...verifiedTutors, ...verifiedAcademies];
    return [...combined]
      .sort((a, b) => scoreProvider(b) - scoreProvider(a))
      .slice(0, 8);
  }, [verifiedTutors, verifiedAcademies]);

  return (
    <section className="featured-section academies">
      <div className="featured-container">
        <ProviderGrid
          title="Verified Providers"
          subtitle="Tutors and training centers with confirmed credentials"
          providers={verifiedProviders}
          loading={loadingVerified}
          showJoinedDate
          emptyMessage="No verified providers available at the moment."
        />

        <Link to="/search" className="featured-view-all-btn">
          Explore More Providers →
        </Link>
      </div>
    </section>
  );
};

export default FeaturedProvidersSection;
